import React, { useState, useMemo, useEffect, useCallback } from 'react';
import clsx from 'clsx';

import { ProductBundleItem } from './ProductBundleItem';
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import Select from '@src/stories/elements/Select/Select';

// Type
import { ILiquidProduct, ILiquidSellingPlanGroups } from '@src/types/product';

import { addIconsToListItems } from '@src/stories/ecommerce/Product/ProductSellingGroups/ProductSellingGroups';

interface BundleProduct {
  id: number;
  handle: string;
  title: string;
  price: string;
  selected_or_first_available_variant_id: string;
  selling_plan_groups: ILiquidSellingPlanGroups;
  metafields: any;
}

interface Bundle {
  most_popular: string;
  product_name: string;
  products: BundleProduct[];
  single_item_price: string;
}

interface SubscriptionTabProps {
  currentProduct: ILiquidProduct;
  bundle: Bundle;
  settings: any;
  usp_content?: string;
  usp_bullet_icon?: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  onProductSelect: (productData: any) => void;
  onSubscriptionSelect: (productData: any) => void;
  isInTile?: boolean; 
}

const SubscriptionTab = ({ currentProduct, bundle, settings, usp_content, usp_bullet_icon, isInTile,  isLoading, setIsLoading, onProductSelect, onSubscriptionSelect }: SubscriptionTabProps) => {
  const [selectedProduct, setSelectedProduct] = useState<ILiquidProduct | null>(currentProduct);
  
  // Helper function to get default selling plan ID
  const getDefaultSellingPlanId = (product: ILiquidProduct | null): number | null => {
    if (!product?.selling_plan_groups?.[0]?.selling_plans) return null;
    
    const plans = product.selling_plan_groups[0].selling_plans;
    const every3MonthsPlan = plans.find(
      plan => plan.name.toLowerCase().includes('3 month')
    );
    return every3MonthsPlan ? every3MonthsPlan.id : (plans[0]?.id ?? null);
  };
  
  const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<number | null>(() => 
    getDefaultSellingPlanId(currentProduct)
  );


  useEffect(() => {
    const defaultPlanId = getDefaultSellingPlanId(currentProduct);
    if(defaultPlanId) handleClickSellingPlanOption(defaultPlanId.toString());
  }, []);


  const sellingPlanOptions = useMemo(() => {
    if (!selectedProduct) return [];

    return selectedProduct?.selling_plan_groups[0]?.selling_plans?.map((plan) => ({
      name: plan.name,
      value: plan.id
    }));
  }, [selectedProduct]);

  const handleClickSellingPlanOption = (val: string | { target: { name: string; value: string } }) => {
    const value = Number(val);
    setSelectedSellingPlanId(value);
    onSubscriptionSelect(value);

    const formSellingPlanFields = document.querySelectorAll(
      '.add-to-cart-block .form-selling-plan-id'
    ) as NodeListOf<HTMLInputElement>;

    formSellingPlanFields.forEach((formInput) => {
      formInput.value = val as string;
    });
  };

  const handleClickBundleItem = ({ product, prices }) => {
    setSelectedProduct(product);
    if (onProductSelect) onProductSelect({ product, prices });

    const formVariantFields = document.querySelectorAll(
      '.add-to-cart-block .form-variant-id'
    ) as NodeListOf<HTMLInputElement>;

    formVariantFields.forEach((formInput) => {
      formInput.value = product.selected_or_first_available_variant_id;
    });
     
    if (isInTile) return;
    if (currentProduct.handle !== product.handle) {
        window.location.href = `/products/${product.handle}?tab=subscribe`;
    }
    
  };

  
  const [renderedItems, setRenderedItems] = useState<number[]>([]);

  const handleBundleItemRender = useCallback((index: number) => {
    setRenderedItems(prev => prev.includes(index) ? prev : [...prev, index]);
  }, []);

  useEffect(() => {
    setRenderedItems([]);
  }, [bundle, selectedProduct, selectedSellingPlanId]);


  return (
    <div>
      <Select
        options={sellingPlanOptions}
        selected={selectedSellingPlanId}
        placeholder={settings?.frequency_placeholder}
        allowDeselect={true}
        onChange={handleClickSellingPlanOption}
      />

      <div className='product-selling-groups__onetime-option mt-5'>
        {bundle.products.map((product, index) => {

          let productSpecificPlanId = selectedSellingPlanId;
          
          if (selectedSellingPlanId && selectedProduct?.selling_plan_groups?.[0]?.selling_plans) {
            const selectedPlan = selectedProduct.selling_plan_groups[0].selling_plans.find(
              plan => plan.id === selectedSellingPlanId
            );
            
            if (selectedPlan) {
              const matchingPlan = product.selling_plan_groups?.[0]?.selling_plans?.find(
                plan => plan.name.toLowerCase() === selectedPlan.name.toLowerCase()
              );
              
              if (matchingPlan) {
                productSpecificPlanId = matchingPlan.id;
              }
            }
          }
          
          return (
            <ProductBundleItem
              key={product.id}
              index={index}
              product={product}
              bundle={bundle}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
              isInTile={isInTile}
              currentProduct={currentProduct}
              selectedOption={selectedProduct?.id}
              onClick={handleClickBundleItem}
              type='subscription'
              sellingPlanId={productSpecificPlanId}
              onRender={() => handleBundleItemRender(index)} 
            />
          );
        })}
      { usp_content  && (  
            <div className={clsx('product-selling-groups__usp-content', {
                    'custom-icon': usp_bullet_icon !== '',
                    'one-column': renderedItems.length % 2 === 0 && renderedItems.length > 0
                  })}>
              <Paragraph
                text={usp_content ? addIconsToListItems(usp_content, usp_bullet_icon) : ''}
                type={'b4'}
                classes="rich-content"
              />
            </div>
      )}
      </div>
    </div>
  );
};
export default SubscriptionTab;
